import loggerService, { LoggerLevel } from '../common/infrastructure/log';

/**
 * localStorage key the e2e suite writes (before the app loads) to force flag values.
 * Value is JSON: `{ "<flag-key>": true | false | "<variant>" }`.
 *
 * The name is deliberately screaming/non-production-looking: this hook ships in every
 * build (prod e2e needs it too), so it must be obvious in devtools that it is a test hook
 * and never a real user setting.
 */
export const E2E_FEATURE_FLAG_OVERRIDES_KEY = '__E2E_TEST_FEATURE_FLAG_OVERRIDES__';

/**
 * Read flag overrides for automated tests / manual QA, to pass as PostHog
 * `bootstrap.featureFlags` at init.
 *
 * Why bootstrap (not the server): the app is `opt_out_capturing_by_default: true` and only
 * opts in after CookieYes consent — which the automated e2e never grants — so PostHog stays
 * opted-out and **never requests feature flags** (verified: no `/flags` request is ever made,
 * flags stay empty). Bootstrapping forces the flags client-side with no network fetch, and in
 * the test nothing later overrides them because PostHog never fetches.
 *
 * Active in **all environments** (the e2e suite also runs against production with a dedicated
 * test tenant), gated only by the presence of the key — which no real user sets. The effect
 * is purely client-side and self-scoped, and must never be used to force a flag that gates a
 * server-side boundary.
 */
export function getE2eFeatureFlagOverrides(): Record<string, boolean | string> | undefined {
  try {
    const raw = window.localStorage.getItem(E2E_FEATURE_FLAG_OVERRIDES_KEY);
    if (!raw) return undefined;
    const flags = JSON.parse(raw) as Record<string, boolean | string>;
    if (flags && typeof flags === 'object' && Object.keys(flags).length > 0) {
      // Loud on purpose: if this fires in a real user session, feature flags are being
      // forced client-side by a test hook and the session's flag state is not authoritative.
      loggerService.log(
        LoggerLevel.WARN,
        `[e2e] feature-flag overrides ACTIVE via localStorage["${E2E_FEATURE_FLAG_OVERRIDES_KEY}"] — test hook, not a real user setting. Flags are client-side only and must never gate a server-side boundary.`,
        flags
      );
      return flags;
    }
  } catch (error) {
    // Malformed override JSON: don't break the app for real users, but surface it loudly
    // so a misconfigured test override is diagnosable during test development.
    loggerService.log(
      LoggerLevel.WARN,
      `[e2e] failed to parse localStorage["${E2E_FEATURE_FLAG_OVERRIDES_KEY}"] — ignoring override.`,
      error
    );
  }
  return undefined;
}
